/**
 * SINGLE SOURCE OF TRUTH for illustration-style technique descriptions.
 *
 * Both the page/cover render (BookGenerationService.getStyleDescription /
 * getLocalizedStyleDescription), the character-portrait render
 * (characters/stylePrompts.getStylePrompt) and the analysis art-direction prompt
 * (which is fed the same description) read from HERE — so a character reference is
 * drawn in the EXACT same style language as the pages it appears on. Divergent
 * copies used to drift (e.g. portraits said "Studio Ghibli", pages said "modern
 * anime" → the reference dragged pages off-style).
 *
 * Leaf module: no imports from services, so anyone can consume it without cycles.
 */

/** English technique text per style id (the authoritative description). */
const STYLE_TECHNIQUE_EN: Record<string, string> = {
  watercolor:
    'Traditional watercolor painting style. Use soft, translucent color washes with visible ' +
    'brush strokes and organic, feathered edges. Colors should bleed and blend naturally into ' +
    'each other. Include wet-on-wet techniques with subtle granulation and paper texture. ' +
    'The palette should feel soft and harmonious, with delicate gradients and a dreamy, ' +
    'hand-painted quality typical of classic children\'s book watercolor illustrations.',

  cartoon:
    'Modern animated cartoon style similar to Disney Junior or Pixar shorts. Use bold black ' +
    'outlines, vibrant saturated colors, and simple rounded shapes. Characters should have ' +
    'large expressive eyes, exaggerated proportions, and cute appealing features. ' +
    'Expressions should be dynamic and readable. Use flat colors with simple cel-shading ' +
    'and clean vector-like aesthetics.',

  comic:
    // Comic craft (pilot v2, 2026-10-30): flat cel colour and a clean ink line read as a
    // comic page (vs the storybook look of v1) came from these words plus explicit negatives.
    'Children\'s comic book art: bold black ink outlines with clean, even line weight, FLAT cel ' +
    'colours with hard-edged shadow shapes, a bright limited palette, simple readable shapes and ' +
    'expressive cartoon characters. Dramatic angles and dynamic action poses; speed lines, motion ' +
    'puffs and impact stars for movement. No watercolor, no gradients, no painterly texture, no ' +
    'storybook illustration look.',

  // Comic sub-styles by age (experiment 2026-10-30, tmp/comics/agestyles/notes.md).
  // 'comic' above IS the kids sub-style (3–6) — old books keep their id. These words
  // are shared by the pages, the cover and the character references: the style of a
  // reference carries into every picture drawn from it, so each sub-style needs refs
  // drawn in its own look (the portrait prompt reads this same text).
  'comic-adventure':
    'Clean-line European adventure comic (ligne claire): thin uniform black ink line of even ' +
    'weight, flat colours with no shading and no gradients, semi-realistic proportions with ' +
    'expressive cartoon faces, lively action poses, detailed carefully drawn backgrounds in clear ' +
    'perspective. No painterly texture, no watercolor, no halftone, no heavy black shadows.',

  'comic-superhero':
    // Halftone dots are asked for but NOT reliable on Grok (the experiment got hatching and
    // grain, never dot screens) — nothing downstream may depend on them.
    'American superhero comic book art: heroic dynamic poses, dramatic low-angle foreshortening, ' +
    'bold confident inking with heavy spot blacks and hard dramatic shadows, halftone dot shading, ' +
    'saturated punchy colours, rim light, glowing energy effects. Characters keep child-friendly ' +
    'faces; animals and companions stay friendly, never angry or scary. No painterly texture, no ' +
    'watercolor, no photo-realism, no gore.',

  'comic-manga':
    'Black-and-white Japanese manga art: clean fine ink lines, big expressive eyes and exaggerated ' +
    'emotional faces, grey screentone dot shading, solid black hair accents, white highlights. ' +
    // Dev run 3: manga Teremok came out as naturalistic wildlife without clothes — the
    // technique said nothing about character design and the colour-free cards had lost the garments.
    'Stylised manga CHARACTER design: simplified anatomy, big eyes, expressive poses; a talking animal is an ' +
    'upright, expressive manga character wearing its outfit, never naturalistic wildlife. Clothes keep their ' +
    'shapes and patterns as light, dark or screentoned areas. ' +
    'Monochrome only: black ink and grey screentone on white paper, no colour. No painterly ' +
    'texture, no computer-rendered CGI look.',

  realistic:
    'Painterly realism in a digital illustration style. Use natural proportions with ' +
    'detailed brushwork and textures on clothing, hair, and environments. Include ' +
    'believable lighting with soft shadows and ambient occlusion. Characters should ' +
    'look lifelike but still warm and approachable for children — illustrated realism ' +
    'similar to high-quality children\'s book covers, oil-painting feel. ' +
    'NOT a photograph, NOT photographic, no camera-captured look, no DSLR rendering. ' +
    'Rich details in backgrounds and clothing.',

  fairytale:
    'Enchanted fairytale illustration style reminiscent of classic European fairy tale books. ' +
    'Use soft, luminous colors with ethereal golden glow effects and delicate light rays. ' +
    'Include intricate decorative borders and ornamental details. Add subtle magical elements: ' +
    'sparkles, floating particles, soft bokeh. Backgrounds should have dreamy atmospheric ' +
    'perspective. Style similar to classic Grimm fairy tale illustrations with romantic, ' +
    'magical atmosphere and rich storytelling details.',

  minimalist:
    'Clean Scandinavian minimalist illustration style. Use simple geometric shapes, ' +
    'limited color palette (2-4 colors maximum), and plenty of negative space. ' +
    'Characters should be stylized with essential features only - no unnecessary details. ' +
    'Focus on clear silhouettes, bold graphic elements, and modern flat design aesthetics. ' +
    'Similar to contemporary Nordic children\'s book illustrations with sophisticated simplicity.',

  coloring:
    'Professional PRINT-READY children\'s coloring book page. STRICT LINE ART ONLY: ' +
    'bold, smooth, CONTINUOUS black contour outlines of consistent THICK line weight (like a felt-tip pen) ' +
    'on a PURE WHITE background. Every shape must be a fully CLOSED region so it can be coloured inside the lines — ' +
    'no broken or open outlines, no floating strokes. ' +
    'ZERO colour, ZERO shading, ZERO gradients, ZERO grey, ZERO hatching / cross-hatching / stippling / texture. ' +
    'ZERO solid black fills — outline everything, never fill (hair, eyes and clothing are OUTLINED shapes, not black blobs). ' +
    'Big, simple, generously-spaced shapes with plenty of open white area to colour; friendly rounded forms; ' +
    'age-appropriate LOW detail (ages 4-8) — recognizable and cheerful, never busy or tiny. ' +
    'Flat 2D, front-on, no 3D perspective, no cast shadows, no background clutter (a few simple ground/prop lines at most). ' +
    'The main subject fills ~70-80% of the frame, centered, with a clean margin. Think classic Crayola / printable coloring sheet.',

  '3d':
    'Modern 3D animated-movie style, like a Pixar / DreamWorks film. Render characters and scenes ' +
    'as polished stylized 3D: soft volumetric shading, rounded appealing forms, smooth clean surfaces, ' +
    'subtle subsurface skin, warm cinematic key-and-fill lighting with gentle ambient occlusion and ' +
    'soft depth-of-field. Big, warm, expressive faces and a rich but friendly colour palette. ' +
    'A high-quality STYLIZED 3D CARTOON — clearly NOT a photograph and NOT photorealistic.',

  papercut:
    'Paper cut-out collage illustration style — a rich, tactile Eric Carle / Ezra Jack Keats look. ' +
    'Build EVERY character and object out of layered pieces of hand-cut, hand-painted textured paper: ' +
    'visible paper grain and painted brush texture on each piece, torn and scissor-cut edges, flat ' +
    'solid colours, and clear soft DROP SHADOWS between overlapping layers so the collage feels ' +
    'physically stacked. Bold, simple, joyful shapes; warm saturated colours; a handcrafted, ' +
    'construction-paper storybook feel — NOT smooth digital art, NOT painterly, NOT 3D.',

  plush:
    'Soft plush-toy / felt illustration style. Render every character as a cuddly stuffed toy: ' +
    'fuzzy fabric and felt textures, visible stitched seams, button or embroidered eyes, soft rounded ' +
    'over-stuffed forms, tiny fabric wrinkles, gentle soft studio lighting with soft shadows. ' +
    'Cozy, huggable, handmade-toy charm in a warm palette — clearly NOT a photograph, a toy-like world.',

  anime:
    'Unmistakable MODERN JAPANESE ANIME / MANGA style — a frame from a contemporary anime TV series ' +
    'or anime movie (shonen / shoujo). MANDATORY on EVERY living character: LARGE eyes with big glossy irises ' +
    'and several bright catch-light highlights; small, sharp, simplified nose and mouth; thin CRISP ' +
    'clean anime linework; flat two-tone CEL-SHADING with hard-edged shadow shapes; glossy anime hair ' +
    'drawn in bold distinct strands with sharp specular highlights. Vivid saturated anime colours, ' +
    'dramatic anime lighting, expressive emotive anime faces; backgrounds may add sparkles, light rays ' +
    'or sakura. It MUST look like a Japanese anime frame — NOT a soft painterly Western children\'s-book ' +
    'illustration, NOT Studio-Ghibli naturalism, NOT a generic cartoon, NOT 3D, NOT a photograph.',
};

/**
 * THE style catalogue: every id we can draw, in the order a parent is offered
 * them (parent appeal, not alphabet).
 *
 * It lives here because this is where a style's technique text lives: an id
 * without technique text is a style we cannot draw, and the test beside this
 * file fails if the two ever disagree. Four copies of this list used to exist
 * — the wizard's own array, the type union, the character portraits enum and
 * the campaign's shortlist — and they had drifted to 10, 11, 7 and 4 entries,
 * so the same product offered a different palette on every screen.
 */
export const ILLUSTRATION_STYLES = [
  'watercolor',
  'cartoon',
  '3d',
  'fairytale',
  'coloring',
  'papercut',
  'plush',
  'realistic',
  'comic',
  'comic-adventure',
  'comic-superhero',
  'comic-manga',
  'anime',
  'minimalist',
] as const;

export type IllustrationStyleId = (typeof ILLUSTRATION_STYLES)[number];

/**
 * Styles kept out of every picker.
 *
 * 'minimalist' stays in the catalogue — books were drawn in it and an admin can
 * still set it — but two colours and a silhouette is not what a parent orders a
 * keepsake in, and it was never worth a tile.
 */
export const HIDDEN_ILLUSTRATION_STYLES: readonly IllustrationStyleId[] = ['minimalist'];

/** What any picker offers — the catalogue minus the hidden ones. */
export const OFFERED_ILLUSTRATION_STYLES = ILLUSTRATION_STYLES.filter(
  (id) => !HIDDEN_ILLUSTRATION_STYLES.includes(id),
) as readonly IllustrationStyleId[];

export function isIllustrationStyle(value: string): value is IllustrationStyleId {
  return (ILLUSTRATION_STYLES as readonly string[]).includes(value);
}

/** True for a style a parent may actually pick. */
export function isOfferedIllustrationStyle(value: string): value is IllustrationStyleId {
  return (OFFERED_ILLUSTRATION_STYLES as readonly string[]).includes(value);
}

/** The authoritative English technique text for a style (fallback: fairytale). */
export function styleTechniqueEN(style: string): string {
  return STYLE_TECHNIQUE_EN[style.toLowerCase()] || STYLE_TECHNIQUE_EN.fairytale;
}

/**
 * PAGE-ONLY composition rules a style adds on top of its technique. Kept apart
 * from the technique text on purpose: the technique is shared by pages, the
 * cover, the character-reference portraits and the wizard preview tiles, and a
 * layout rule leaking into those rendered a character reference as a 4-panel
 * comic page with a caption box (the dragon-as-comic-page bug). Only the
 * interior-page render appends this. Empty for styles without one.
 */
const STYLE_PAGE_LAYOUT_EN: Record<string, string> = {
  // The full comic page (grid catalog, cameras, lettering) is built by comicPage.ts for
  // every page render; this line is only the fallback for a caller without a plan.
  comic:
    'LAYOUT (MANDATORY): compose EACH page as a MULTI-PANEL comic-book page — 3 to 4 panels with ' +
    'white gutters and thick black borders, each a distinct beat with its own camera (at least one ' +
    'close-up and one low or high angle). NEVER a single full-page illustration. All text is ' +
    'hand-lettered in UPPERCASE inside balloons or one caption box.',
};

/** Page-only layout rule for a style ('' when the style has none). */
export function stylePageLayoutEN(style: string): string {
  if (isComicStyle(style)) return STYLE_PAGE_LAYOUT_EN.comic;
  return STYLE_PAGE_LAYOUT_EN[style.toLowerCase()] || '';
}

// ── comics ──────────────────────────────────────────────────────────────────

/**
 * The comic family: one id per sub-style. 'comic' is the kids sub-style (3–6) and
 * stays the id old books carry. EVERY "is this a comic?" question in the app asks
 * isComicStyle — a `style === 'comic'` check silently turned a superhero book back
 * into a picture book (prose band, no script, prose in the PDF).
 */
export const COMIC_STYLES = ['comic', 'comic-adventure', 'comic-superhero', 'comic-manga'] as const;
export type ComicStyleId = (typeof COMIC_STYLES)[number];

export function isComicStyle(style: string | null | undefined): style is ComicStyleId {
  return !!style && (COMIC_STYLES as readonly string[]).includes(style.toLowerCase());
}

/**
 * What a comic sub-style changes in the SCRIPT and on the PAGE (comicScript.ts,
 * comicPage.ts). Plain data, no imports — layout ids are comicPage's catalog keys
 * (a test checks they exist).
 */
export interface ComicCraft {
  id: ComicStyleId;
  /** youngest age the sub-style is meant for (the picker's hint) */
  minAge: number;
  /** opens the page prompt: the work surface ("…comic book page, N panels") — `{n}` = panel count */
  surface: string;
  /** catalog layouts this sub-style uses, in page-to-page order */
  layouts: readonly string[];
  /** pictures per page the script may ask for */
  panels: { min: number; max: number };
  /** how movement is drawn */
  motion: string;
  /** the speech balloon shape (thought/shout keep their own) */
  balloon: string;
  /** the caption box */
  captionBox: string;
  /** the sound word */
  sfx: string;
  /** camera preference the script writer gets */
  cameraBias: string;
  /** tone + craft notes for the script writer */
  scriptNotes: string;
  /** lettering budget per page (tighter than the measured model limits) */
  limits: { balloonWords: number; balloonsPerPage: number; letteredWordsPerPage: number };
  /**
   * Pages are PURE black-and-white. The lesson of the manga v2 render: a colour word
   * anywhere in the panel or invariant text ("red hoodie") came back as spot colour,
   * so in such a style no colour word may reach a page prompt.
   */
  monochromePages: boolean;
  /** the cover's technique when it differs from the pages' (manga: a colour volume cover) */
  coverLook?: string;
  /** extra line for every page of this sub-style */
  pageNote?: string;
}

const COMIC_CRAFT: Record<ComicStyleId, ComicCraft> = {
  comic: {
    id: 'comic',
    minAge: 3,
    surface:
      "Children's comic book page, {n} panels, bold black ink outlines, flat cel colours, white gutters, " +
      'thick black panel borders. Round soft chunky shapes, big heads and big friendly eyes, simple uncluttered ' +
      'backgrounds. No watercolor, no gradients, no painterly texture, no storybook illustration look.',
    layouts: ['3-tiers', 'wide-top+2', 'big+inset', '2+wide-bottom', 'splash+2', 'tall-left+2-right'],
    panels: { min: 2, max: 3 },
    motion: 'soft motion puffs and little stars show the movement',
    balloon: 'big round white speech balloon',
    captionBox: 'yellow caption box with a thin black border',
    sfx: 'big bold round friendly outlined letters',
    cameraBias: 'mostly clear wide and medium shots; one close-up of a face per page; nothing scary',
    scriptNotes:
      'For ages 3–6: one simple beat per panel, very few words, nothing scary, warm and funny; ' +
      'a gentle sound word is welcome.',
    limits: { balloonWords: 10, balloonsPerPage: 3, letteredWordsPerPage: 24 },
    monochromePages: false,
  },
  'comic-adventure': {
    id: 'comic-adventure',
    minAge: 7,
    surface:
      'European adventure comic album page, {n} panels in a clean rectangular grid, thin black panel borders, ' +
      'white gutters, ligne claire: thin even ink line, flat colours, detailed backgrounds in clear perspective. ' +
      'No watercolor, no gradients, no painterly texture, no storybook illustration look.',
    layouts: ['wide-top+3', '2x2', 'tall-left+2-right', 'strip-5', 'wide-top+2', '2+wide-bottom'],
    panels: { min: 3, max: 5 },
    motion: 'crisp speed lines show the movement',
    balloon: 'white oval speech balloon',
    captionBox: 'pale yellow rectangular caption box with a thin black border',
    sfx: 'crisp bold outlined letters',
    cameraBias: 'establishing wide shots of the place, medium shots for dialogue, one close-up per page',
    scriptNotes:
      'For ages 7–10: real but calm action; the hero wins by courage and cleverness, not superpowers; ' +
      'the place matters — show it.',
    limits: { balloonWords: 12, balloonsPerPage: 5, letteredWordsPerPage: 35 },
    monochromePages: false,
  },
  'comic-superhero': {
    id: 'comic-superhero',
    minAge: 9,
    surface:
      'American superhero comic book page, {n} dynamic panels with slanted diagonal borders, one panel breaking ' +
      'its border, thick black panel borders, white gutters, heavy spot blacks, saturated colours. ' +
      'No watercolor, no painterly texture, no storybook illustration look.',
    layouts: ['slanted-4', 'splash+2', 'diagonal-3', 'wide-top+3', 'big+inset', '2x2'],
    panels: { min: 2, max: 4 },
    motion: 'explosive speed lines, impact bursts and glowing energy streaks show the power',
    balloon: 'white speech balloon with a bold black outline',
    captionBox: 'yellow rectangular caption box with a bold black border',
    // (dev run 8, Теремок p3: a stray «2» beside the «ХРУСТЬ!» burst — no digit in a drawn-lettering line, «3D» included)
    sfx: 'huge bursting outlined letters with a deep extruded shadow, inside an impact burst',
    cameraBias:
      'dramatic low angles and worm\'s-eye views for the hero, extreme close-ups of a determined face, ' +
      'one big impact panel per page',
    scriptNotes:
      'The CHILD is the hero: give the kid the power beat (a catch, a lift, a leap — with an energy aura, the ' +
      'hood flying like a cape), described concretely ("both hands under the falling corner"). No violence, ' +
      'nobody gets hurt; the danger is the situation. Animals and companions stay friendly, never angry or scary.',
    limits: { balloonWords: 12, balloonsPerPage: 5, letteredWordsPerPage: 35 },
    monochromePages: false,
    pageNote:
      'Friendly characters stay FRIENDLY in every panel: animals and companions never roar, bare claws or ' +
      'look angry or scary. No violence, nobody gets hurt.',
  },
  'comic-manga': {
    id: 'comic-manga',
    minAge: 10,
    surface:
      'Black-and-white manga page, {n} panels with cinematic varied paneling (tall panels, wide panels, a small ' +
      'close-up inset), thin black panel borders, white gutters, grey screentone shading, monochrome only. ' +
      'No painterly texture, no computer-rendered CGI look, no colour.',
    layouts: ['manga-5', 'tall-left+2-right', 'strip-5', 'splash+2', '2x2'],
    panels: { min: 3, max: 5 },
    motion: 'dense speed lines and focus lines show the movement',
    balloon: 'tall white oval speech balloon',
    captionBox: 'white rectangular caption box with a thin black border',
    sfx: 'bold hand-drawn black sound-effect letters drawn into the art',
    cameraBias:
      'extreme close-ups of the eyes and faces for emotion, focus lines, tall action panels; at most a few words',
    scriptNotes:
      'For ages 10+: emotional faces (sweat drops, wide eyes) carry the story; few words, more close-ups. ' +
      'Write the panel actions WITHOUT any colour words (the pages are black and white).',
    limits: { balloonWords: 10, balloonsPerPage: 4, letteredWordsPerPage: 28 },
    monochromePages: true,
    coverLook:
      'Manga volume cover in FULL COLOUR (the story pages inside are black and white): clean anime-style cel ' +
      'colours, fine ink lines, big expressive eyes, the same stylised, clothed character designs as the pages. ' +
      'Not black-and-white, not greyscale. No painterly texture, no computer-rendered CGI look.',
    pageNote:
      'PURE BLACK-AND-WHITE: black ink and grey screentone only, no colour anywhere, not even accents.',
  },
};

/** The craft of a comic sub-style; any non-comic id gets the kids craft (callers check isComicStyle first). */
export function comicCraft(style: string | null | undefined): ComicCraft {
  return isComicStyle(style) ? COMIC_CRAFT[style.toLowerCase() as ComicStyleId] : COMIC_CRAFT.comic;
}

/** True when the PAGES of this style are black-and-white (manga) — no colour word may reach them. */
export function monochromePages(style: string | null | undefined): boolean {
  return isComicStyle(style) && comicCraft(style).monochromePages;
}

/**
 * The cover's technique text: the style's own, except where a sub-style has a
 * different cover (manga: full colour around black-and-white pages).
 */
export function coverTechniqueEN(style: string): string {
  return (isComicStyle(style) && comicCraft(style).coverLook) || styleTechniqueEN(style);
}

// ── prominence (how many characters are drawn large at once) ────────────────

/**
 * How many characters a STYLE lets be prominent (speaker / foreground, drawn
 * large) at once. A composition property of the style, read by the storyboard
 * prompt, the comic script prompt and their validators — never a type split in
 * code. It is NOT the image model's reference budget: who gets an image of their
 * own and who rides in a lineup is the worker's call (ref_budget.py), and a
 * character in a lineup is drawn in front just as well (owner, 2026-10-31).
 * Both fields are optional on any style; a style without them has no cap.
 */
export interface StyleProminence {
  /** at most this many characters drawn large in one panel (a panelled style) */
  maxProminentPerPanel?: number;
  /** at most this many speaker + foreground characters on one page */
  maxProminentPerPage?: number;
}

const STYLE_PROMINENCE: Partial<Record<IllustrationStyleId, StyleProminence>> = {
  comic: { maxProminentPerPanel: 3 },
  'comic-adventure': { maxProminentPerPanel: 3 },
  'comic-superhero': { maxProminentPerPanel: 3 },
  'comic-manga': { maxProminentPerPanel: 3 },
};

/** The style's prominence caps ({} = none). */
export function styleProminence(style: string | null | undefined): StyleProminence {
  return STYLE_PROMINENCE[String(style ?? '').toLowerCase() as IllustrationStyleId] ?? {};
}

/**
 * The storyboard's cast-role rule for this style (English): roles follow the
 * scene; the style's own caps, when it has any, are stated.
 */
export function castRoleRuleEN(style: string | null | undefined): string {
  const { maxProminentPerPanel: panel, maxProminentPerPage: page } = styleProminence(style);
  const parts = [
    'ROLES follow the SCENE: as many speakers and foreground characters as the scene needs.',
  ];
  if (page) {
    parts.push(
      `This book's style holds AT MOST ${page} speaker + foreground characters per page; everyone else ` +
      'present is "background", never dropped.',
    );
  }
  if (panel) {
    parts.push(
      `This book's style is drawn in panels with at most ${panel} characters large in one panel; a page ` +
      'may still have more speakers and foreground characters — the panels share them out.',
    );
  }
  return parts.join(' ');
}

/**
 * The comic sub-style a book of this age group gets by default, read from the
 * group's lower bound ('1-3' | '3-5' | '5-7' | '7-10' | '10+'): up to 5 → kids
 * (3–6), 6–8 → adventure, 9+ → superhero (the owner's «feel like a superhero»).
 * Manga is never a default: black-and-white pages are an opt-in for 10+.
 */
export function defaultComicStyleForAge(ageGroup: string | null | undefined): ComicStyleId {
  const m = String(ageGroup ?? '').match(/(\d+)/);
  if (!m) return 'comic';
  const from = Number(m[1]);
  if (from <= 5) return 'comic';
  if (from <= 8) return 'comic-adventure';
  return 'comic-superhero';
}

/** The comic sub-styles in the order a picker lists them (youngest first). */
export const COMIC_STYLES_BY_AGE: readonly ComicStyleId[] = ['comic', 'comic-adventure', 'comic-superhero', 'comic-manga'];

/**
 * The tiles of a tile picker: the offered styles with the comic family folded into
 * ONE «Комикс» tile ('comic'); the picker shows the sub-styles under it once it is chosen.
 */
export const STYLE_PICKER_TILES = OFFERED_ILLUSTRATION_STYLES.filter(
  (id) => !isComicStyle(id) || id === 'comic',
) as readonly IllustrationStyleId[];

/** Colour names (EN + the story languages) — removed from black-and-white page text. */
// Whole words (Latin-script languages): "red", "red-haired", never "redraw" or "tangled".
const COLOUR_WORDS_WHOLE = [
  'red', 'reddish', 'crimson', 'scarlet', 'maroon', 'pink', 'rosy', 'orange', 'yellow', 'golden', 'gold',
  'green', 'emerald', 'olive', 'lime', 'teal', 'turquoise', 'cyan', 'blue', 'navy', 'azure', 'indigo',
  'violet', 'purple', 'lilac', 'lavender', 'magenta', 'brown', 'beige', 'ginger', 'auburn',
  'blond', 'blonde', 'chestnut', 'copper', 'bronze', 'silver', 'colou?red', 'colou?rful', 'multicolou?red',
  'pastel', 'rainbow',
  'rot', 'rote', 'roten', 'roter', 'rotes', 'blau', 'blaue', 'blauen', 'blauer', 'grün', 'grüne', 'grünen',
  'gelb', 'gelbe', 'gelben', 'braun', 'braune', 'braunen', 'rouge', 'rouges', 'bleu', 'bleue', 'bleus',
  'vert', 'verte', 'verts', 'jaune', 'jaunes', 'rojo', 'roja', 'rojos', 'azul', 'azules', 'verde', 'verdes',
  'amarillo', 'amarilla',
];
// Stems (Slavic languages inflect): "красн" covers красный/красная/красной…
const COLOUR_STEMS = [
  'красн', 'ал(?:ый|ая|ое|ые|ого|ой|ую|ым)', 'розов', 'оранжев', 'рыж', 'ж[её]лт', 'жовт', 'золот', 'зел[её]н',
  'салатов', 'бирюзов', 'голуб', 'син(?:ий|яя|ее|ие|его|ей|юю|им|ими|их|ем|ього|ім)', 'фиолетов', 'сиренев',
  'лилов', 'пурпурн', 'коричнев', 'бежев', 'каштанов', 'медн', 'серебрист', 'серебрян', 'разноцветн', 'цветн',
  'кольоров', 'червон', 'помаранчев', 'блакитн', 'фіолетов', 'золотист',
  'červen', 'modr', 'zelen', 'žlut', 'hněd', 'czerwon', 'niebiesk', 'zielon', 'żółt', 'brązow',
];
const COLOUR_WORDS = new RegExp(
  `(?<![\\p{L}-])(?:(?:${COLOUR_WORDS_WHOLE.join('|')})(?:-\\p{L}+)*(?![\\p{L}])|(?:${COLOUR_STEMS.join('|')})[\\p{L}-]*)`,
  'giu',
);

/**
 * The text with every colour word removed — OUTSIDE quotes only, so a quoted name
 * («Красная Шапочка») or a lettered string is never touched. For black-and-white
 * pages: the invariants and panel text must not name a colour.
 */
export function stripColourWords(text: string): string {
  if (!text) return text;
  return text
    .split(/("[^"\n]*"|«[^»\n]*»|“[^”\n]*”)/)
    .map((part, i) =>
      i % 2 === 1
        ? part
        : part
            .replace(COLOUR_WORDS, '')
            // tidy what the cut leaves: "a , hoodie" → "a hoodie", doubled spaces
            .replace(/[ \t]+([,.;:)])/g, '$1')
            .replace(/([(,])\s*,/g, '$1')
            .replace(/[ \t]{2,}/g, ' ')
            // a line that began with a colour word does not begin with a space
            .replace(i === 0 ? /^[ \t]+/gm : /(?!)/, ''),
    )
    .join('');
}

// the same colour words, also right after a hyphen ("dark-blue" must lose its "blue" too)
const COLOUR_WORDS_AFTER_HYPHEN = new RegExp(
  `(?<=[\\p{L}]-)(?:(?:${COLOUR_WORDS_WHOLE.join('|')})(?![\\p{L}])|(?:${COLOUR_STEMS.join('|')})[\\p{L}]*)`,
  'giu',
);
const LIGHT_TONE = /^(?:yellow|golden|gold|pink|rosy|beige|blond|blonde|silver|pastel|lilac|lavender|azure|cyan|lime|gelb|jaune|amarill|ж[её]лт|жовт|золот|розов|бежев|голуб|блакитн|серебр|сиренев|салатов|žlut|żółt)/iu;
const PATTERN_TONE = /^(?:colou?red|colou?rful|multicolou?red|rainbow|разноцветн|цветн|кольоров)/iu;

/**
 * Black-and-white wording that KEEPS the thing: every colour word becomes a TONE — "light",
 * "dark" or "patterned" — outside quotes ("a red scarf" → "a dark scarf"). Dev run 3 (manga):
 * the colour-free cards had dropped the garments with their colours, and the characters came
 * out unclothed; a tone keeps the scarf and tells the inker how to screentone it.
 */
export function monochromeTones(text: string): string {
  if (!text) return text;
  return text
    .split(/("[^"\n]*"|«[^»\n]*»|“[^”\n]*”)/)
    .map((part, i) =>
      i % 2 === 1
        ? part
        : part
            .replace(COLOUR_WORDS_AFTER_HYPHEN, (w) => (LIGHT_TONE.test(w) ? 'light' : 'dark'))
            .replace(COLOUR_WORDS, (w) => (PATTERN_TONE.test(w) ? 'patterned' : LIGHT_TONE.test(w) ? 'light' : 'dark'))
            // "dark-dark" / "light light" from a compound colour ("dark-blue", "light blue")
            .replace(/\b(dark|light)(?:[\s-]+(?:dark|light))+\b/giu, '$1'),
    )
    .join('');
}

/**
 * Shared do/don't the render prompts append, so living vs inanimate handling is
 * identical everywhere (page, cover, portrait). Only LIVING beings get faces.
 */
export const ANTHROPOMORPHISM_NOTE =
  'Only LIVING beings (people, animals, creatures the story treats as characters) have a face, ' +
  'eyes, expressions or human-like limbs. An inanimate object, plant, food or place (a turnip, a ' +
  'house, the sun) is a REALISTIC object in this style — NO face, eyes, mouth or limbs — unless the ' +
  'story itself makes it talk or act like a person.';

/**
 * The SHARED authoritative style-lock block — identical mechanics for pages,
 * the cover AND any style preview (wizard tiles) so they never diverge.
 * `subject` is what is being rendered ("page" / "cover" / "preview").
 */
export function buildStyleLock(
  styleDescription: string,
  subject: 'page' | 'cover' | 'preview',
  /** The story's tradition inside this technique (artDirection.styleTradition). */
  tradition?: string,
): string {
  return (
    `=== STYLE (AUTHORITATIVE) ===\n` +
    `${styleDescription}\n` +
    (tradition?.trim()
      // Inside the technique, never instead of it: the line above still decides
      // what the picture IS, this says which tradition of it the story asks for.
      ? `WITHIN that technique, this story's tradition: ${tradition.trim()}\n`
      : '') +
    `This STYLE is defined HERE and is non-negotiable — render EVERY character, face and ` +
    `element of the ${subject} in it. The character reference images define each character's ` +
    `IDENTITY ONLY (face shape, hair, colours, proportions, clothing) — MATCH the identity, ` +
    `but do NOT copy a reference's own art style or rendering; REDRAW it in the style above. ` +
    `If a reference looks like a different style (e.g. a plain cartoon face), still render ` +
    `that character fully in the style above.\n` +
    `${ANTHROPOMORPHISM_NOTE}\n\n`
  );
}

/**
 * FINAL rendering lock — the LAST thing the model reads, which it weights most.
 * Re-asserts the authoritative technique so a soft/diffuse art-direction note
 * above cannot pull the render off-style (anime → Ghibli was the failure).
 */
export function buildFinalStyleLock(style: string, styleDescription: string): string {
  return (
    `\n=== FINAL RENDERING LOCK (overrides ANY softer / diffuse / painterly note above) ===\n` +
    `Render the whole image in the "${style}" technique EXACTLY as defined — ${styleDescription} ` +
    `Do not soften or change the technique to match a reference or a lighting note.\n`
  );
}
